import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // Car Drift Racers Series
    'car-drift-racers-2': ['drift-boss', 'drift-hunters', 'drift-dudes', 'jelly-drift', 'retro-drift', 'sling-drift', 'driftmania'],

    // Drift Series
    'drift-boss': ['car-drift-racers-2', 'drift-hunters', 'drift-dudes', 'jelly-drift', 'retro-drift', 'sling-drift', 'driftmania'],
    'drift-hunters': ['car-drift-racers-2', 'drift-boss', 'drift-dudes', 'jelly-drift', 'retro-drift', 'sling-drift', 'driftmania'],
    'drift-dudes': ['car-drift-racers-2', 'drift-boss', 'drift-hunters', 'jelly-drift', 'retro-drift', 'sling-drift', 'driftmania'],
    'jelly-drift': ['car-drift-racers-2', 'drift-boss', 'drift-hunters', 'drift-dudes', 'retro-drift', 'sling-drift', 'driftmania'],
    'retro-drift': ['car-drift-racers-2', 'drift-boss', 'drift-hunters', 'drift-dudes', 'jelly-drift', 'sling-drift', 'driftmania'],
    'sling-drift': ['car-drift-racers-2', 'drift-boss', 'drift-hunters', 'drift-dudes', 'jelly-drift', 'retro-drift', 'driftmania'],
    'driftmania': ['car-drift-racers-2', 'drift-boss', 'drift-hunters', 'drift-dudes', 'jelly-drift', 'retro-drift', 'sling-drift'],

    // Highway Racer Series
    'highway-racer': ['highway-racer-2', 'highway-racer-3d'],
    'highway-racer-2': ['highway-racer', 'highway-racer-3d'],
    'highway-racer-3d': ['highway-racer', 'highway-racer-2'],

    // Extreme Off-Road Cars Series
    'extreme-off-road-cars': ['extreme-off-road-cars-3-cargo', 'ultimate-offroad-cars-2', '4x4-drive-offroad'],
    'extreme-off-road-cars-3-cargo': ['extreme-off-road-cars', 'ultimate-offroad-cars-2', '4x4-drive-offroad'],
    'ultimate-offroad-cars-2': ['extreme-off-road-cars', 'extreme-off-road-cars-3-cargo', '4x4-drive-offroad'],

    // Fly Car Stunt Series
    'fly-car-stunt': ['fly-car-stunt-2', 'fly-car-stunt-4', 'mega-ramp-car-stunts', 'stunt-car-challenge-3'],
    'fly-car-stunt-2': ['fly-car-stunt', 'fly-car-stunt-4', 'mega-ramp-car-stunts', 'stunt-car-challenge-3'],
    'fly-car-stunt-4': ['fly-car-stunt', 'fly-car-stunt-2', 'mega-ramp-car-stunts', 'stunt-car-challenge-3'],

    // Madalin Cars Series
    'madalin-stunt-cars-2': ['madalin-cars-multiplayer', 'car-parking-multiplayer', 'cars-simulator', '3d-car-simulator'],
    'madalin-cars-multiplayer': ['madalin-stunt-cars-2', 'car-parking-multiplayer', 'cars-simulator', '3d-car-simulator'],

    // Hover Racer Series
    'hover-racer': ['hover-racer-drive', 'cyber-cars-punk-racing', 'merge-cyber-racers'],
    'hover-racer-drive': ['hover-racer', 'cyber-cars-punk-racing', 'merge-cyber-racers'],

    // Merge Racers Series
    'merge-cyber-racers': ['merge-round-racers', 'blumgi-racers'],
    'merge-round-racers': ['merge-cyber-racers', 'blumgi-racers'],
    'blumgi-racers': ['merge-cyber-racers', 'merge-round-racers'],

    // Hill Climb Racing Series
    'hill-climb-racing': ['hill-climb-racing-2', 'hill-climb-racing-lite', 'up-hill-racing-2'],
    'hill-climb-racing-2': ['hill-climb-racing', 'hill-climb-racing-lite', 'up-hill-racing-2'],
    'hill-climb-racing-lite': ['hill-climb-racing', 'hill-climb-racing-2', 'up-hill-racing-2'],
    'up-hill-racing-2': ['hill-climb-racing', 'hill-climb-racing-2', 'hill-climb-racing-lite'],

    // Eggy Car Series
    'eggy-car': ['eggy-car-2', 'car-climb-racing', 'car-eats-car-winter-adventure'],
    'eggy-car-2': ['eggy-car', 'car-climb-racing', 'car-eats-car-winter-adventure'],

    // Wheelie Bike Series
    'wheelie-bike': ['wheelie-bike-2', 'crazy-bikes', 'stunt-biker-3d'],
    'wheelie-bike-2': ['wheelie-bike', 'crazy-bikes', 'stunt-biker-3d'],

    // 18 Wheeler Truck Parking Series
    '18-wheeler-truck-parking': ['18-wheeler-truck-parking-2', '18-wheeler-cargo-simulator-2'],
    '18-wheeler-truck-parking-2': ['18-wheeler-truck-parking', '18-wheeler-cargo-simulator-2'],
    '18-wheeler-cargo-simulator-2': ['18-wheeler-truck-parking', '18-wheeler-truck-parking-2'],

    // Monster Truck Racing Series
    'monster-truck-racing-arena': ['racing-monster-truck', '3d-monster-truck-skyroads', 'real-simulator-monster-truck'],
    'racing-monster-truck': ['monster-truck-racing-arena', '3d-monster-truck-skyroads', 'real-simulator-monster-truck'],
    '3d-monster-truck-skyroads': ['monster-truck-racing-arena', 'racing-monster-truck', 'real-simulator-monster-truck'],
    'real-simulator-monster-truck': ['monster-truck-racing-arena', 'racing-monster-truck', '3d-monster-truck-skyroads'],

    // City Driving Series
    'city-car-driving-stunt-master': ['extreme-car-driving-simulator', 'ultimate-car-driving', 'real-city-driving-2', 'evo-city-driving', 'real-cars-in-city'],
    'extreme-car-driving-simulator': ['city-car-driving-stunt-master', 'ultimate-car-driving', 'real-city-driving-2', 'evo-city-driving', 'real-cars-in-city'],
    'ultimate-car-driving': ['city-car-driving-stunt-master', 'extreme-car-driving-simulator', 'real-city-driving-2', 'evo-city-driving', 'real-cars-in-city'],
    'real-city-driving-2': ['city-car-driving-stunt-master', 'extreme-car-driving-simulator', 'ultimate-car-driving', 'evo-city-driving', 'real-cars-in-city'],
    'evo-city-driving': ['city-car-driving-stunt-master', 'extreme-car-driving-simulator', 'ultimate-car-driving', 'real-city-driving-2', 'real-cars-in-city'],

    // Burnout Drift Series
    'burnout-extreme-drift-2': ['burnout-drift-hilltop', 'drift-boss', 'drift-hunters', 'driftmania'],
    'burnout-drift-hilltop': ['burnout-extreme-drift-2', 'drift-boss', 'drift-hunters', 'driftmania'],

    // Slope Racing Series
    'slope-racing-3d': ['race-master-3d', 'furious-racing-3d', 'top-speed-racing-3d'],

    // Race Master / Racing Games
    'race-master-3d': ['furious-racing-3d', 'top-speed-racing-3d', 'highway-racer-3d'],
    'furious-racing-3d': ['race-master-3d', 'top-speed-racing-3d', 'highway-racer-3d'],
    'top-speed-racing-3d': ['race-master-3d', 'furious-racing-3d', 'highway-racer-3d'],

    // Car Simulator Series
    'cars-simulator': ['3d-car-simulator', 'car-simulator-arena', 'edys-car-simulator', 'real-cars-in-city'],
    '3d-car-simulator': ['cars-simulator', 'car-simulator-arena', 'edys-car-simulator', 'real-cars-in-city'],
    'car-simulator-arena': ['cars-simulator', '3d-car-simulator', 'edys-car-simulator', 'real-cars-in-city'],
    'edys-car-simulator': ['cars-simulator', '3d-car-simulator', 'car-simulator-arena', 'real-cars-in-city'],

    // Racing Monster / Truck Series
    'endless-truck': ['car-eats-car-winter-adventure', 'real-simulator-monster-truck', 'racing-monster-truck'],

    // Parkour / Runner Race Series
    'bridge-race': ['stair-race-3d', 'shortcut-race', 'parkour-race', 'survival-race'],
    'stair-race-3d': ['bridge-race', 'shortcut-race', 'parkour-race', 'survival-race'],
    'shortcut-race': ['bridge-race', 'stair-race-3d', 'parkour-race', 'survival-race'],
    'parkour-race': ['bridge-race', 'stair-race-3d', 'shortcut-race', 'survival-race'],

    // Survival Race Series
    'survival-race': ['survival-race-v2', 'bridge-race', 'shortcut-race'],
    'survival-race-v2': ['survival-race', 'bridge-race', 'shortcut-race'],

    // Runner Series
    'subway-runner': ['subway-princess-runner', 'tomb-runner', 'dark-runner', 'fred-running'],
    'subway-princess-runner': ['subway-runner', 'tomb-runner', 'dark-runner', 'fred-running'],
    'tomb-runner': ['subway-runner', 'subway-princess-runner', 'dark-runner', 'fred-running'],
    'dark-runner': ['subway-runner', 'subway-princess-runner', 'tomb-runner', 'fred-running'],

    // Bike / Motorcycle Racing Series
    'highway-bike-simulator': ['sports-bike-racing', 'gp-moto-racing', 'crazy-bikes', 'stunt-biker-3d'],
    'sports-bike-racing': ['highway-bike-simulator', 'gp-moto-racing', 'crazy-bikes', 'stunt-biker-3d'],
    'gp-moto-racing': ['highway-bike-simulator', 'sports-bike-racing', 'crazy-bikes', 'stunt-biker-3d'],
    'crazy-bikes': ['highway-bike-simulator', 'sports-bike-racing', 'gp-moto-racing', 'stunt-biker-3d'],

    // Off-Road / 4x4 Series
    '4x4-drive-offroad': ['extreme-off-road-cars', 'extreme-off-road-cars-3-cargo', 'off-road-rain-cargo-simulator'],
    'off-road-rain-cargo-simulator': ['4x4-drive-offroad', 'extreme-off-road-cars', 'extreme-off-road-cars-3-cargo'],

    // Stunt Racing Series
    'stunt-car-challenge-3': ['mega-ramp-car-stunts', 'fly-car-stunt', 'fly-car-stunt-2', 'fly-car-stunt-4'],
    'mega-ramp-car-stunts': ['stunt-car-challenge-3', 'fly-car-stunt', 'fly-car-stunt-2', 'fly-car-stunt-4'],

    // City Bike Stunt Series
    'city-bike-stunt-2': ['bike-trials-offroad-1', 'stunt-biker-3d', 'bicycle-stunts-3d'],
    'bike-trials-offroad-1': ['city-bike-stunt-2', 'stunt-biker-3d', 'bicycle-stunts-3d'],
    'bicycle-stunts-3d': ['city-bike-stunt-2', 'bike-trials-offroad-1', 'stunt-biker-3d'],

    // Parking Series
    'car-parking-multiplayer': ['18-wheeler-truck-parking', '18-wheeler-truck-parking-2', 'cars-simulator'],

    // 3D Racing Series
    '3d-arena-racing': ['race-master-3d', 'furious-racing-3d', 'top-speed-racing-3d'],
    '100-meters-race': ['furious-racing-3d', 'race-master-3d', 'top-speed-racing-3d'],

    // Car Stunt / Driving Collection
    'crazy-cars': ['super-star-car', 'death-car', 'cars-simulator', '3d-car-simulator'],
    'super-star-car': ['crazy-cars', 'death-car', 'cars-simulator', '3d-car-simulator'],
    'death-car': ['crazy-cars', 'super-star-car', 'cars-simulator', '3d-car-simulator'],

    // Car Climb / Physics Driving
    'car-climb-racing': ['eggy-car', 'eggy-car-2', 'hill-climb-racing', 'up-hill-racing-2'],

    // Truck / Cargo Driving
    '18-wheeler-cargo-simulator-2': ['18-wheeler-truck-parking', '18-wheeler-truck-parking-2', 'off-road-rain-cargo-simulator'],
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      "drift-hunters",
      "drift-dudes",
      "jelly-drift",
      "retro-drift",
      "sling-drift",
      "driftmania",
      "burnout-extreme-drift-2",
      "burnout-drift-hilltop",
      "race-master-3d",
      "furious-racing-3d",
      "highway-racer-3d",
      "highway-racer",
      "top-speed-racing-3d",
      "crazy-cars",
      "super-star-car"
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
